import { type DealDocument, type DealDocumentsResult, DOC_CATEGORIES, DOC_STATUSES, groupQuotations, isOriginalCategory, isQuotationDocument, parsePaymentTerms, PRESALES_STATUSES } from '@crm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type DragEvent, type FormEvent, useRef, useState, type ReactNode } from 'react';
import { droppedFiles, dropZone } from '../lib/dropFiles';
import { TechProgress } from './TechProgress';
import { useSession } from '../lib/session';
import { api, ApiError, errorDetail } from '../lib/api';
import { askDuplicate, checkDuplicates } from '../lib/duplicates';
import { formatDate, formatDateTime, riyadhToday } from '../lib/format';
import { flyTo, toast } from '../lib/motion';
import { CopyButton } from './CopyButton';
import { confirmDialog } from '../lib/dialogs';
import { Icon } from './Icon';
import { PaymentTermsPicker } from './PaymentTerms';
/** Categories pre-sales fills (supplier and customer quotations, submittals): not offered in the sales upload list. */
const SALES_HIDDEN: string[] = ['supplier_quotations', 'customer_quotations', 'submittals'];
/** Choice in 'Put files in' for a folder to create. */
const NEW = '__new__';
const errMsg = errorDetail;
const catLabel = (c: string) => DOC_CATEGORIES.find((x) => x.value === c)?.label ?? c;
const size = (n: number) => (n >= 1024 ** 3 ? `${(n / 1024 ** 3).toFixed(1)} GB` : n >= 1024 ** 2 ? `${(n / 1024 ** 2).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

/** Browser → CRM storage with progress; resolves with the ETag (needed to assemble a large upload). */
function putWithProgress(url: string, body: Blob, headers: Record<string, string>, onProgress: (loaded: number) => void): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => onProgress(e.loaded);
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve(xhr.getResponseHeader('ETag') ?? '') : reject(new Error(`Upload to the CRM failed (${xhr.status}).`)));
    xhr.onerror = () => reject(new Error('The connection was interrupted while uploading. Try again.'));
    xhr.send(body);
  });
}

/** Large files: one part at a time, each part retried up to 3 times, so a dropped connection doesn't restart the file. */
async function uploadParts(base: string, documentId: string, file: File, partSize: number, onProgress: (pct: number) => void) {
  const parts: { partNumber: number; etag: string }[] = [];
  const count = Math.ceil(file.size / partSize);
  for (let n = 1; n <= count; n++) {
    const blob = file.slice((n - 1) * partSize, Math.min(n * partSize, file.size));
    for (let attempt = 1; ; attempt++) {
      try {
        const { url } = await api<{ url: string }>(`${base}/${documentId}/part`, { method: 'POST', body: { partNumber: n } });
        const etag = await putWithProgress(url, blob, {}, (loaded) => onProgress(Math.round((((n - 1) * partSize + loaded) / file.size) * 100)));
        parts.push({ partNumber: n, etag });
        break;
      } catch (e) {
        if (attempt >= 3) throw e;
        await new Promise((r) => setTimeout(r, 3000 * attempt));
      }
    }
  }
  return parts;
}

/** One file into the deal's Microsoft 365 folder (kept as a new revision if the name exists); resolves with its document id. */
export async function uploadDealFile(opportunityId: string, file: File, category: string, subfolder: string, onProgress: (pct: number) => void,
  /** The same file already on the deal (same name and size): use it instead of refusing (submittals). */
  reuseExisting = false): Promise<string> {
  const base = `/api/opportunities/${opportunityId}/documents`;
  // Already in the deal's OneDrive folder (saved there directly)? The person chooses before anything is uploaded.
  const found = (await checkDuplicates(`/api/opportunities/${opportunityId}/documents-check`, [file]))?.[0];
  if (found?.length) {
    const choice = await askDuplicate(file, found, true);
    if (choice === 'cancel') throw new Error('Upload cancelled.');
    if (choice === 'use') {
      const r = await api<{ documentId: string }>(`/api/opportunities/${opportunityId}/documents-use-existing`, { method: 'POST', body: { itemId: found.find((m) => m.same)!.itemId }, quiet: true });
      onProgress(100);
      return r.documentId;
    }
  }
  const contentType = /^[\w.+-]+\/[\w.+-]+$/.test(file.type) ? file.type : 'application/octet-stream';
  let start: { documentId: string; uploadUrl?: string; headers?: Record<string, string>; multipart?: { partSize: number } };
  try {
    start = await api<typeof start>(base, {
      method: 'POST', body: { category, name: file.name, sizeBytes: file.size, contentType, newRevision: true, subfolder },
    });
  } catch (e) {
    const d = e instanceof ApiError ? (e.details as { reason?: string; documentId?: string } | undefined) : undefined;
    if (reuseExisting && e instanceof ApiError && e.status === 409 && d?.reason === 'duplicate' && d.documentId) { onProgress(100); return d.documentId; }
    throw e;
  }
  if (start.multipart) {
    const parts = await uploadParts(base, start.documentId, file, start.multipart.partSize, onProgress);
    await api(`${base}/${start.documentId}/complete`, { method: 'POST', body: { parts } });
  } else {
    await putWithProgress(start.uploadUrl!, file, start.headers ?? {}, (loaded) => onProgress(Math.round((loaded / file.size) * 100)));
    await api(`${base}/${start.documentId}/complete`, { method: 'POST', body: {} });
  }
  return start.documentId;
}

/** A quotation file (the rule is shared with the server: Quotation Ready lists the same files to sales). */
export const isQuotationFile = (d: DealDocument) => isQuotationDocument(d);
const newestFirst = (a: DealDocument, b: DealDocument) => String(b.msModifiedAt ?? b.uploadedAt ?? '').localeCompare(String(a.msModifiedAt ?? a.uploadedAt ?? ''));

/**
 * On the deal page: the quotations pre-sales uploaded (Upload quotation), newest first, each with ⬇ Download, so sales
 * can send them to the customer.
 */
/** The deal's quotations (Download, Remove, Upload): the top of the one "Quotations & documents" box. */
export function DealQuotations({ opportunityId }: { opportunityId: string }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['deal-docs', opportunityId], queryFn: () => api<DealDocumentsResult>(`/api/opportunities/${opportunityId}/documents`),
    // While a quotation is on its way to OneDrive: checked every few seconds, so it shows as soon as it is there.
    refetchInterval: (query) => (query.state.data?.documents.some((d) => d.status === 'pending' || d.status === 'uploading') ? 4000 : false),
  });
  const retry = useMutation({
    mutationFn: (docId: string) => api(`/api/opportunities/${opportunityId}/documents/${docId}/retry`, { method: 'POST', body: {} }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['deal-docs', opportunityId] }),
  });
  // Pre-sales (or an administrator) removes a quotation from the CRM; the file stays in OneDrive.
  const remove = useMutation({
    mutationFn: (docId: string) => api(`/api/opportunities/${opportunityId}/quotations/${docId}`, { method: 'DELETE', quiet: true }),
    onSuccess: () => { setErr(null); toast('Quotation removed from the CRM'); },
    onError: (e) => setErr(errMsg(e)),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['deal-docs', opportunityId] }),
  });
  const removeQuote = async (d: DealDocument) => {
    if ((await confirmDialog(`Remove “${d.msName ?? d.fileName}” from the CRM? The file stays in the deal’s OneDrive folder.`))) remove.mutate(d.id);
  };
  // One row per option: its latest revision, the older ones folded under it, copies counted once.
  const groups = groupQuotations((q.data?.documents ?? []).filter(isQuotationFile).sort(newestFirst));
  const olderCount = groups.reduce((a, g) => a + g.older.length, 0);
  // Quotations uploaded here and not in OneDrive yet (on their way, or failed: retry).
  const onTheWay = (q.data?.documents ?? []).filter((d) => d.category === 'customer_quotations' && d.origin !== 'folder' && d.status !== 'verified' && !d.missingAt);
  // Pre-sales on the deal uploads the quotation here (into Customer Quotations), checked for duplicates in OneDrive first.
  const canUploadQuote = !!q.data?.connected && !!q.data.presales.canWork;
  const [pct, setPct] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const upload = async (file: File | undefined) => {
    if (!file) return;
    setErr(null);
    try {
      await uploadDealFile(opportunityId, file, 'customer_quotations', '', setPct);
      toast('Quotation uploaded: sales can download it now');
    } catch (e) { if (errMsg(e) !== 'Upload cancelled.') setErr(errMsg(e)); }
    setPct(null);
    void qc.invalidateQueries({ queryKey: ['deal-docs', opportunityId] });
  };
  return (
    <div className="docs-quotes">
      <div className="docs-quotes-head">
        <h4>Quotations ({q.data ? groups.length : '…'}){olderCount > 0 && <span className="small muted"> · {olderCount} older</span>}</h4>
        <span className="popup-inline">
          {canUploadQuote && (
            <label className={`btn small primary${pct != null ? ' is-loading' : ''}`} title="Upload the quotation (PDF) for this deal: sales download it from here">
              {pct != null ? `Uploading… ${pct}%` : <><Icon name="upload" />Upload quotation</>}
              <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx" hidden disabled={pct != null} onChange={(e) => { void upload(e.target.files?.[0]); e.target.value = ''; }} />
            </label>
          )}
        </span>
      </div>
      {err && <p className="form-error pad-x" role="alert">{err}</p>}
      {onTheWay.length > 0 && (
        <ul className="list">
          {onTheWay.map((d) => (
            <li key={d.id}>
              <span><Icon name="file" tone="pdf" /><b>{d.msName ?? d.fileName}</b>
                <div className={`small ${d.status === 'failed' ? 'form-error' : 'muted'}`}>{d.status === 'failed' ? `Not saved to OneDrive: ${d.lastError ?? 'unknown error'}` : 'Uploading to OneDrive… (shows here when it is saved)'}</div></span>
              {d.status === 'failed' && <button className="btn small" disabled={retry.isPending} onClick={() => retry.mutate(d.id)}><Icon name="refresh" />Retry</button>}
            </li>
          ))}
        </ul>
      )}
      {groups.length ? (
        <ul className="list">
          {groups.map(({ latest, older, copies }) => {
            const row = (d: DealDocument, sub = false) => (
              <div className={`quote-row${sub ? ' quote-older' : ''}`} key={d.id}>
                <span><Icon name="file" tone={sub ? 'muted' : 'pdf'} /><b>{d.msName ?? d.fileName}</b>{d.revision > 1 && <span className="pill"> Rev {d.revision}</span>}
                  <div className="small muted">{d.msModifiedAt || d.uploadedAt ? formatDateTime(d.msModifiedAt ?? d.uploadedAt) : ''}
                    {!sub && copies > 0 && <span title="The same quotation is also in the folder under a slightly different name (spaces / underscores); listed once"> · {copies === 1 ? '1 other copy in the folder' : `${copies} other copies in the folder`}</span>}</div></span>
                <span className="popup-inline">
                  {d.msWebUrl?.startsWith('/api/documents/') && <a className={`btn small${sub ? '' : ' primary'}`} href={d.msWebUrl} title="Download to send to the customer"><Icon name="download" />Download</a>}
                  {q.data?.canRemoveQuotations && <button className="btn small" disabled={remove.isPending} onClick={() => removeQuote(d)}
                    title="Remove from the CRM’s Quotations (the file stays in OneDrive)"><Icon name="trash" />Remove</button>}
                </span>
              </div>
            );
            return (
              <li key={latest.id} className="quote-group">
                {row(latest)}
                {older.length > 0 && (
                  <details className="quote-olders">
                    <summary className="small">{older.length === 1 ? '1 older revision' : `${older.length} older revisions`}</summary>
                    {older.map((d) => row(d, true))}
                  </details>
                )}
              </li>
            );
          })}
        </ul>
      ) : onTheWay.length ? null : (
        <p className="muted pad">{q.isPending ? 'Loading…' : 'No quotation yet. Pre-sales uploads it here with Upload quotation.'}</p>
      )}
    </div>
  );
}

/**
 * On the deal: the supplier prices Purchasing got for its project (chosen ones marked), so pre-sales quotes the next
 * deal with real costs. Shown only when there are any.
 */
export function DealPurchasePrices({ opportunityId }: { opportunityId: string }) {
  const q = useQuery({
    queryKey: ['deal-purchase-prices', opportunityId],
    queryFn: () => api<{ number: string; supplier: string; price: number | null; deliveryDays: number | null; status: string; chosen: boolean; date: string; items: string | null }[]>(`/api/opportunities/${opportunityId}/purchase-prices`),
    retry: false,
  });
  if (!q.data?.length) return null;
  return (
    <section className="panel">
      <header className="panel-head"><h3>Supplier prices from Purchasing ({q.data.length})</h3></header>
      <ul className="list">
        {q.data.map((r, i) => (
          <li key={`${r.number}-${i}`}>
            <span><b>{r.supplier}</b>{r.chosen && <span className="pill appr-approved"> ✓ {r.status === 'ordered' ? 'Ordered' : 'Chosen'}</span>}
              <div className="small muted">{r.number} · {r.items}</div></span>
            <span className="num">{r.price != null ? new Intl.NumberFormat(undefined, { style: 'currency', currency: 'SAR', maximumFractionDigits: 0 }).format(r.price) : '—'}
              <div className="small muted">{r.deliveryDays != null ? `${r.deliveryDays} days · ` : ''}{formatDate(r.date)}</div></span>
          </li>
        ))}
      </ul>
    </section>
  );
}

interface LocalUpload { key: string; name: string; category: string; pct: number; error: string | null }

/**
 * Deal documents: uploaded here, transferred to the company's Microsoft 365 project folder, and linked back.
 * Pre-sales works in that folder; "Refresh" shows what is there now (no two-way synchronization).
 */
export function DealDocuments({ opportunityId }: { opportunityId: string }) {
  const qc = useQueryClient();
  const { user, permissions } = useSession();
  const base = `/api/opportunities/${opportunityId}/documents`;
  const q = useQuery({
    queryKey: ['deal-docs', opportunityId],
    queryFn: () => api<DealDocumentsResult>(base),
    // Follow transfers while any is on its way.
    refetchInterval: (query) => (query.state.data?.documents.some((d) => d.status === 'pending' || d.status === 'uploading') ? 4000 : false),
  });
  // New files go to Pre-Sales Working Files unless another category is chosen.
  const [cat, setCat] = useState('presales_working');
  // Where new files go: '' = the deal folder itself, a folder name, or NEW for a folder to create.
  const [dest, setDest] = useState('');
  // Customer PO category: its number and date, saved on the deal with the upload.
  const [poNumber, setPoNumber] = useState('');
  const [poDate, setPoDate] = useState('');
  // The PO's payment terms (picked, saved one payment per line): the project's billing milestones.
  const [poTerms, setPoTerms] = useState('');
  const termsCheck = parsePaymentTerms(poTerms);
  const [newFolder, setNewFolder] = useState(() => `New files ${new Date().toLocaleDateString('en-GB').replace(/\//g, '-')}`);
  const [uploads, setUploads] = useState<LocalUpload[]>([]);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);  const [handing, setHanding] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement | null>(null);
  const reload = () => {
    void qc.invalidateQueries({ queryKey: ['deal-docs', opportunityId] });
    void qc.invalidateQueries({ queryKey: ['record', 'opportunity', opportunityId] });
  };
  const failed = (e: unknown) => setMsg({ ok: false, text: errMsg(e) });

  const action = useMutation({
    mutationFn: ({ url, method = 'POST', body = {} }: { url: string; method?: string; body?: unknown }) => api(url, { method, body: method === 'DELETE' ? undefined : body }),
    onSuccess: () => { setMsg(null); reload(); },
    onError: failed,
  });

  const uploadOne = async (file: File, category: string, key: string, subfolder: string, newRevision = false): Promise<void> => {
    const set = (patch: Partial<LocalUpload>) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, ...patch } : x)));
    try {
      const contentType = /^[\w.+-]+\/[\w.+-]+$/.test(file.type) ? file.type : 'application/octet-stream';
      const start = await api<{ documentId: string; uploadUrl?: string; headers?: Record<string, string>; multipart?: { partSize: number } }>(base, {
        method: 'POST', body: { category, name: file.name, sizeBytes: file.size, contentType, newRevision, subfolder },
      });
      reload();
      if (start.multipart) {
        const parts = await uploadParts(base, start.documentId, file, start.multipart.partSize, (pct) => set({ pct }));
        await api(`${base}/${start.documentId}/complete`, { method: 'POST', body: { parts } });
      } else {
        await putWithProgress(start.uploadUrl!, file, start.headers ?? {}, (loaded) => set({ pct: Math.round((loaded / file.size) * 100) }));
        await api(`${base}/${start.documentId}/complete`, { method: 'POST', body: {} });
      }
      setUploads((u) => u.filter((x) => x.key !== key));
      reload();
    } catch (e) {
      const reason = e instanceof ApiError ? (e.details as { reason?: string; revision?: number } | undefined) : undefined;
      if (reason?.reason === 'revision' && (await confirmDialog(`${errMsg(e)}\n\nThe earlier file is kept; this one is saved alongside it as revision ${reason.revision}.`))) {
        return uploadOne(file, category, key, subfolder, true);
      }
      set({ error: errMsg(e) });
    }
  };
  const pick = (files: FileList | null) => {
    if (!files?.length) return;
    // A whole folder (Upload a folder…): each file keeps its folder path ("Gabel Omar - ELE/Power/x.dwg").
    pickList([...files].map((f) => ({ file: f, dir: f.webkitRelativePath ? f.webkitRelativePath.split('/').slice(0, -1).join('/') : '' })));
  };
  // Files and folders dragged from the PC onto the upload area.
  const [dragOver, setDragOver] = useState(false);
  const onDrop = async (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    setDragOver(false);
    try { pickList(await droppedFiles(e.dataTransfer)); } catch { setMsg({ ok: false, text: 'Some of the dropped files couldn’t be read. Try Upload files… instead.' }); }
  };
  const pickList = (files: { file: File; dir: string }[]) => {
    if (!files.length) return;
    setMsg(null);
    const subfolder = dest === NEW ? newFolder.trim() : dest;
    if (dest === NEW && !subfolder) { setMsg({ ok: false, text: 'Give the new folder a name.' }); return; }
    // The customer's PO: its number (and date) is saved on the deal with the file, and copied to the project.
    if (cat === 'customer_po') {
      if (!poNumber.trim()) { setMsg({ ok: false, text: 'Type the customer PO number first.' }); if (fileInput.current) fileInput.current.value = ''; return; }
      // The terms are asked with the PO (they become the project's billing milestones): picked, or "Not stated in the PO".
      if (!poTerms.trim()) { setMsg({ ok: false, text: 'Choose the PO’s payment terms, or tick “Not stated in the PO”.' }); if (fileInput.current) fileInput.current.value = ''; return; }
      if (termsCheck.errors.length) { setMsg({ ok: false, text: `Payment terms: ${termsCheck.errors[0]}` }); if (fileInput.current) fileInput.current.value = ''; return; }
      void api(`/api/records/opportunity/${opportunityId}`, { method: 'PATCH', body: { customerPoNumber: poNumber.trim(), customerPoDate: poDate || null, ...(poTerms.trim() ? { paymentTerms: poTerms.trim() } : {}) } })
        .then(() => { reload(); setMsg({ ok: true, text: `Customer PO ${poNumber.trim()} saved on the deal (and its project).` }); })
        .catch(failed);
    }
    // Files from a folder keep their folder path inside the chosen place.
    const list = files.map(({ file: f, dir }, i) => ({ file: f, key: `${Date.now()}-${i}`, folder: [subfolder, dir].filter(Boolean).join('/') }));
    setUploads((u) => [...u, ...list.map(({ file, key, folder }) => ({ key, name: folder ? `${folder}/${file.name}` : file.name, category: cat, pct: 0, error: null }))]);
    // First: any of them already in the deal's OneDrive folder (saved there directly)? Then one after the other, so a
    // large package doesn't saturate the connection.
    const category = cat;
    void (async () => {
      const found = await checkDuplicates(`/api/opportunities/${opportunityId}/documents-check`, list.map((x) => x.file));
      for (const [i, { file, key, folder }] of list.entries()) {
        const matches = found?.[i] ?? [];
        if (matches.length) {
          const choice = await askDuplicate(file, matches, true);
          const drop = () => setUploads((u) => u.filter((x) => x.key !== key));
          if (choice === 'cancel') { drop(); continue; }
          if (choice === 'use') {
            try {
              await api(`/api/opportunities/${opportunityId}/documents-use-existing`, { method: 'POST', body: { itemId: matches.find((m) => m.same)!.itemId }, quiet: true });
              toast('Using the file already in OneDrive');
            } catch (e) { failed(e); }
            drop();
            reload();
            continue;
          }
        }
        await uploadOne(file, category, key, folder);
      }
    })();
    // The new folder now exists for the next files too.
    if (dest === NEW) setDest(subfolder);
    if (fileInput.current) fileInput.current.value = '';
    if (folderInput.current) folderInput.current.value = '';
  };

  if (!q.data) return q.isError ? <section className="panel"><p className="form-error pad">{errMsg(q.error)}</p></section> : null;
  const r = q.data;
  const p = r.presales;
  const done = r.documents.filter((d) => d.status === 'verified');
  const originals = done.filter((d) => isOriginalCategory(d.category));
  const working = done.filter((d) => !isOriginalCategory(d.category));
  const blocking = r.documents.filter((d) => d.origin !== 'folder' && d.status !== 'verified');
  // Uploaded files aren't listed to anyone; only uploads that haven't finished (or failed) are shown.
  const attention = r.documents.filter((d) => d.origin !== 'folder' && d.status !== 'verified');
  const statusLabel = PRESALES_STATUSES.find((s) => s.value === (p.status ?? 'awaiting_documents'))?.label;

  const table = (title: string, docs: DealDocument[], hint: string) => (
    <div className="docs-group">
      <h4>{title} <span className="muted small">{hint}</span></h4>
      {docs.length === 0 ? <p className="muted small">None yet.</p> : (
        <div className="table-wrap flat">
          <table>
            <thead><tr><th>File</th><th>Type</th><th>Size</th><th>Status</th><th>Uploaded</th><th /></tr></thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.id} className="static">
                  <td>
                    <span>{d.msName ?? d.fileName}</span>
                    {d.subfolder && <span className="pill folder" title="Folder inside the deal folder"><Icon name="folder" />{d.subfolder}</span>}
                    {d.revision > 1 && <span className="pill">Rev {d.revision}</span>}
                    {d.afterHandoff && <span className="pill warn">New after handoff</span>}
                    {d.origin === 'folder' && <span className="pill">{d.category === 'presales_working' ? 'Working file' : 'Added in folder'}</span>}
                    {d.origin === 'migrated' && <span className="pill">From attachments</span>}
                    {d.missingAt && <div className="small form-error">Not in the folder any more (moved or deleted in Microsoft 365) since {formatDateTime(d.missingAt)}.</div>}
                    {d.origin === 'folder' && d.msModifiedAt && <div className="small muted">Last edited {formatDateTime(d.msModifiedAt)}</div>}
                  </td>
                  <td className="small">{catLabel(d.category)}</td>
                  <td className="num">{size(d.sizeBytes)}</td>
                  <td>
                    <span className={`doc-status ${d.status}`}>{DOC_STATUSES.find((s) => s.value === d.status)?.label}</span>
                    {d.status === 'uploading' && d.sizeBytes > 0 && <span className="small muted"> {Math.min(100, Math.round((d.bytesSent / d.sizeBytes) * 100))}%</span>}
                    {d.status === 'verified' && d.verifyMethod && <div className="small muted">{d.verifyMethod}</div>}
                    {d.lastError && <div className="small form-error">{d.lastError}</div>}
                    {d.origin !== 'folder' && d.status === 'verified' && <div className="small muted">{d.hasCrmCopy ? 'CRM copy kept' : d.crmCopyRemovedAt ? 'Stored in Microsoft 365 only' : ''}</div>}
                  </td>
                  <td className="small muted">{d.uploadedByName ?? '—'}<br />{formatDateTime(d.uploadedAt)}</td>
                  <td className="actions">
                    {/* Download: fetched from OneDrive by the CRM, so no access to the OneDrive folder is needed. */}
                    {d.status === 'verified' && !d.missingAt && d.msWebUrl?.startsWith('/api/documents/') && (
                      <a className="btn small" href={d.msWebUrl} title="Download this file (from the deal’s OneDrive folder)"><Icon name="download" />Download</a>
                    )}
                    {d.status === 'failed' && r.canUpload && <button className="btn small glow-next" onClick={() => action.mutate({ url: `${base}/${d.id}/retry` })}>Retry</button>}
                    {(d.status === 'failed' || d.status === 'pending') && r.canUpload && (
                      <button className="btn small ghost" onClick={async () => { if ((await confirmDialog(`Discard “${d.fileName}”? It hasn’t reached Microsoft 365.`))) action.mutate({ url: `${base}/${d.id}`, method: 'DELETE' }); }}>Discard</button>
                    )}
                                        {d.hasCrmCopy && d.status === 'verified' && d.origin === 'crm_upload' && p.canHandOff && (
                      <button className="btn small ghost" onClick={async () => { if ((await confirmDialog('Remove the CRM’s copy? The file stays in Microsoft 365.'))) action.mutate({ url: `${base}/${d.id}/remove-crm-copy` }); }}>Remove CRM copy</button>
                    )}
                    {/* A quotation: removed from the CRM only (as on the Quotations panel); the file stays in OneDrive. */}
                    {r.canRemoveQuotations && isQuotationFile(d) && (
                      <button className="btn small" disabled={action.isPending}
                        onClick={async () => { if ((await confirmDialog(`Remove “${d.msName ?? d.fileName}” from the CRM? The file stays in the deal’s OneDrive folder.`))) action.mutate({ url: `/api/opportunities/${opportunityId}/quotations/${d.id}`, method: 'DELETE' }, { onSuccess: () => toast('Quotation removed from the CRM') }); }}><Icon name="trash" />Remove</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <section className="panel deal-docs" aria-labelledby="docs-title">
      <header className="panel-head">
        <h3 id="docs-title">Quotations &amp; documents</h3>
        <div className="actions">
          {/* The deal's OneDrive folder ("SQ123 - Deal name"): opens it in Microsoft 365. */}
          {r.folder
            ? (r.folder.webUrl
              ? <a className="docs-folder" href={r.folder.webUrl} target="_blank" rel="noopener noreferrer" title="Open the deal folder in OneDrive"><Icon name="folder" />{r.folder.name}</a>
              : <span className="docs-folder"><Icon name="folder" />{r.folder.name}</span>)
            : r.connected && <span className="docs-folder none" title="Created with the next SQ number on the first upload; an existing folder named after the deal is linked automatically"><Icon name="folder" />No OneDrive folder linked yet</span>}
          {/* Copy the SQ number (e.g. for an email or the costing file name). */}
          {r.folder && /SQ\s?\d+/i.test(r.folder.name) && <CopyButton text={(/SQ\s?\d+/i.exec(r.folder.name)?.[0] ?? '').replace(/\s/g, '').toUpperCase()} label="SQ number" />}
          <span className={`presales-badge ${p.status ?? 'awaiting_documents'}`} title={p.note ? `Pre-sales: ${p.note}` : undefined}>{statusLabel}</span>
        </div>
      </header>
      {/* The quotations first (sales download them from here); the folder's other files below. */}
      <DealQuotations opportunityId={opportunityId} />
      {/* What pre-sales is waiting for, right under the badge (not only at the bottom of the panel). */}
      {p.status === 'clarification' && (
        <div className="banner warn pad-x clar-ask" role="status">
          <b>Pre-sales needs:</b> {p.note || 'documents or answers (no details written).'}
          <span className="small"> Upload the missing files below, then <b>Send the answer to pre-sales</b> (under Pre-sales handoff).</span>
        </div>
      )}
      <p className="muted small pad-x">
        Files are uploaded to the company’s Microsoft 365 project folder{r.folder ? <> <b>{r.folder.name}</b></> : ' (created with the next number on the first upload)'} and linked here.
        Changes made in Microsoft 365 aren’t synchronized back: upload files here.
      </p>
      {!r.connected && <p className="banner">Microsoft 365 storage isn’t connected yet. An administrator can connect it in Setup → Integrations.</p>}

      {r.canUpload && (
        // Files or folders dragged from the PC can be dropped anywhere on this area.
        <div className={`docs-upload drop-zone${dragOver ? ' drag-over' : ''}`} {...dropZone(setDragOver, (e) => void onDrop(e))}>
          {dragOver && <div className="drop-hint" aria-hidden="true">Drop the files or folders here to upload them</div>}
          <label className="field"><span className="label">Document category</span>
            <select value={cat} onChange={(e) => setCat(e.target.value)}>
              {/* Quotations and submittals come from pre-sales (OneDrive / My Desk); sales download them, not upload. */}
              {DOC_CATEGORIES.filter((c) => !SALES_HIDDEN.includes(c.value)).map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </label>
          <label className="field"><span className="label">Put files in</span>
            <select value={dest} onChange={(e) => setDest(e.target.value)}>
              <option value="">{r.folder ? `${r.folder.name} (main folder)` : 'The deal folder (main)'}</option>
              {[...new Set([...r.subfolders, ...(dest && dest !== NEW ? [dest] : [])])].map((s) => <option key={s} value={s}>{s}</option>)}
              <option value={NEW}>+ New folder…</option>
            </select>
          </label>
          {cat === 'customer_po' && (<>
            <label className="field"><span className="label">Customer PO no. *</span>
              <input value={poNumber} maxLength={100} placeholder="e.g. PO-2026-0457" onChange={(e) => setPoNumber(e.target.value)} />
            </label>
            <label className="field"><span className="label">PO date</span>
              <input type="date" value={poDate} onChange={(e) => setPoDate(e.target.value)} />
            </label>
            <div className="field wide"><span className="label">Payment terms of this PO *</span>
              <PaymentTermsPicker value={poTerms} onChange={setPoTerms} />
              {poTerms.trim() && termsCheck.errors.length > 0 && <span className="form-error small">{termsCheck.errors[0]}</span>}
            </div>
          </>)}
          {dest === NEW && (
            <label className="field"><span className="label">New folder name</span>
              <input value={newFolder} maxLength={100} onChange={(e) => setNewFolder(e.target.value)} placeholder="e.g. Addendum 1" />
            </label>
          )}
          <label className="btn primary">
            Upload files…
            <input ref={fileInput} type="file" multiple hidden onChange={(e) => pick(e.target.files)} />
          </label>
          {/* A whole folder (with the folders inside it): select the folder and press Upload in the window. */}
          <label className="btn" title="Choose a folder: all its files (and the folders inside it) are uploaded, keeping the folder names">
            <Icon name="folder" />Upload a folder…
            <input ref={(el) => { folderInput.current = el; el?.setAttribute('webkitdirectory', ''); }} type="file" multiple hidden onChange={(e) => pick(e.target.files)} />
          </label>
          <span className="small muted">Any size (up to Microsoft’s {Math.round(r.maxMb / 1024)} GB per file); big files go up in parts. Same file twice is refused; a changed file is kept as a new revision. You can also drag files or folders from your PC onto this area.</span>
        </div>
      )}
      {/* Linking an old deal's existing folder is done by pre-sales in My Desk (⏭ Coming next). */}
      {uploads.length > 0 && (
        <ul className="docs-local">
          {uploads.map((u) => (
            <li key={u.key}>
              <b>{u.name}</b> <span className="muted small">{catLabel(u.category)}</span>
              {u.error ? <span className="form-error small"> {u.error} <button className="link-btn" onClick={() => setUploads((x) => x.filter((y) => y.key !== u.key))}>Dismiss</button></span>
                : <span className="small"> Uploading to the CRM… {u.pct}%</span>}
            </li>
          ))}
        </ul>
      )}
      {msg && <p className={msg.ok ? 'notice pad-x' : 'form-error pad-x'} role="status">{msg.text}</p>}

      {done.length > 0 && (
        <div className="docs-summary pad-x">
          <span className="doc-status verified">✓</span>
          <span><b>{done.length}</b> file{done.length === 1 ? '' : 's'} uploaded{r.folder ? <> (<b>{r.folder.name}</b>)</> : null}: {originals.length} tender, {working.length} working / quotations.</span>
        </div>
      )}
      {/* The quotations (customer quotation category, or a PDF named EST… / Quotation…, or in Final Offers, wherever pre-sales saved it),
          newest first, each with Download; every other file under "Show all files". */}
      {(() => {
        const newest = newestFirst;
        return (
          <>
            {done.length > 0 && (
              <details className="docs-all pad-x">
                <summary>Show all files ({done.length})</summary>
                {table('All files', [...done].sort(newest), '(tender documents, drawings, working files, quotations)')}
              </details>
            )}
          </>
        );
      })()}
      {attention.length > 0 && table('Uploads not finished', attention, '(still uploading or failed)')}

      <div className="docs-group handoff">
        <h4>Pre-sales handoff</h4>
        {p.progress && <TechProgress p={p.progress} lead={p.userName} support={p.support.map((s) => s.name)} dealId={opportunityId}
          // Accept and start for the lead engineer (or an administrator): the same Start as on My Desk.
          onAccept={p.status === 'ready' && !!p.userId && p.canWork && (p.userId === user.id || permissions.system.manageSetup)
            ? () => action.mutate({ url: `/api/opportunities/${opportunityId}/presales`, body: { status: 'in_progress' } }) : undefined}
          acceptBusy={action.isPending} />}
        {p.userName && (
          <p className="small">
            Working on it: <b>{p.userName}</b>{p.support.length > 0 && <> (lead) with <b>{p.support.map((s) => s.name).join(', ')}</b> (support)</>}{p.tenderDeadline && <> · customer submission {formatDate(p.tenderDeadline)}</>}
            {p.note && <><br /><span className={p.status === 'clarification' ? 'form-error' : 'muted'}>Pre-sales: {p.note}</span></>}
          </p>
        )}
        {/* The message also shows when no engineer is named yet (it used to be hidden then). */}
        {!p.userName && p.note && <p className="small"><span className={p.status === 'clarification' ? 'form-error' : 'muted'}>Pre-sales: {p.note}</span></p>}
        {!p.userName && p.status === 'ready' && (
          <p className="small">With the <b>pre-sales team</b>: the pre-sales manager is assigning the engineer and the internal deadline.{p.tenderDeadline && <> Customer submission {formatDate(p.tenderDeadline)}.</>}</p>
        )}
        <div className="actions">
          {/* Only when sales has something to send: the first hand-off, an answer to pre-sales' question, or a revision
              after the quotation. While the deal is with pre-sales (to acknowledge / in progress) there is nothing to press. */}
          {p.canHandOff && !handing && !['ready', 'in_progress'].includes(p.status ?? '') && (
            // Every document verified: the button glows softly as the next step.
            <button className={`btn primary${blocking.length === 0 && p.status !== 'quotation_ready' ? ' glow-next' : ''}`} disabled={blocking.length > 0} title={blocking.length ? 'Wait until every document is verified (or retry/discard failed ones).' : undefined} onClick={() => setHanding(true)}>
              {p.status === 'clarification' ? 'Send the answer to pre-sales' : p.status === 'quotation_ready' ? 'Ask pre-sales for a revision' : 'Ready for Pre-Sales'}
            </button>
          )}
        </div>
        {/* Old deals (documents already in OneDrive) are marked by pre-sales in My Desk (⏭ Coming next). */}
        {blocking.length > 0 && p.canHandOff && <p className="small muted">Handoff waits for {blocking.length} document{blocking.length === 1 ? '' : 's'} still pending, uploading or failed.</p>}
        {handing && <HandoffForm opportunityId={opportunityId} initial={p} onDone={(text) => {
          // The deal visibly travels to pre-sales (the Pre-sales badge pulses), then the next step is said.
          flyTo(document.querySelector('.handoff-form .btn.primary'), document.querySelector('.presales-badge'), 'folder');
          window.setTimeout(() => toast('You’ll be told when pre-sales starts', { hint: true }), 900);
          setHanding(false); setMsg({ ok: true, text }); reload();
        }} onCancel={() => setHanding(false)} />}
        {p.handoffs.length > 0 && (
          <ul className="handoff-history small">
            {p.handoffs.map((h) => (
              <li key={h.id}>{formatDateTime(h.createdAt)}: {h.createdByName} → <b>{h.presalesUserName}</b>{h.acknowledgedAt ? ` · started ${formatDateTime(h.acknowledgedAt)}` : h.presalesUserName.startsWith('the pre-sales team') ? ' · waiting for the manager to assign it' : ' · not started yet'}</li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function HandoffForm({ opportunityId, initial, onDone, onCancel }: {
  opportunityId: string; initial: DealDocumentsResult['presales']; onDone: (text: string) => void; onCancel: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const send = () => api<{ repeated: boolean; assigned: boolean }>(`/api/opportunities/${opportunityId}/handoff`, { method: 'POST', body });
      try {
        return await send();
      } catch (e) {
        // A linked folder whose files the CRM hasn't listed yet: check the folder (as Refresh does), then try again.
        if (!(e instanceof ApiError) || !e.message.startsWith('Upload the tender documents first')) throw e;
        try { await api(`/api/opportunities/${opportunityId}/documents-refresh`, { method: 'POST', body: {} }); } catch { throw e; }
        try { return await send(); } catch (again) {
          if (again instanceof ApiError && again.message.startsWith('Upload the tender documents first')) {
            throw new Error('The deal’s OneDrive folder has no files yet. Upload the tender documents (Upload files…), then hand off.');
          }
          throw again;
        }
      }
    },
    onSuccess: (r) => onDone(r.repeated
      ? 'Handoff details updated. No new task was created: no new documents since the last handoff.'
      : r.assigned
        ? 'Handed to pre-sales: the engineer already on this deal was told, with the deal and folder links.'
        : 'Handed to the pre-sales team: the pre-sales manager assigns the engineer and the internal deadline. You will see who is working on it here and at the top of the deal.'),
    onError: (e) => setError(errMsg(e)),
  });
  const todayYmd = riyadhToday();
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const fd = new FormData(e.currentTarget);
    save.mutate({ tenderDeadline: fd.get('tenderDeadline') || null, quoteDeadline: fd.get('quoteDeadline') || null, scope: String(fd.get('scope') ?? '') });
  };
  return (
    <form className="handoff-form" onSubmit={submit}>
      <p className="small muted wide">The deal goes to the <b>pre-sales team</b>. The pre-sales manager assigns the engineer (and support engineers) and confirms the internal deadline in My Desk; you are told who is working on it.</p>
      <label className="field"><span className="label">Customer submission deadline{initial.isTender ? ' *' : ''}</span>
        <input name="tenderDeadline" type="date" required={initial.isTender} defaultValue={initial.tenderDeadline ?? ''} /></label>
      <label className="field" title="When you need the quotation. The pre-sales manager confirms or changes it."><span className="label">Quotation needed by (optional)</span>
        {/* The previous hand-off's date is offered only while it is still ahead; never a date in the past. */}
        <input name="quoteDeadline" type="date" min={todayYmd} defaultValue={initial.quoteDeadline && initial.quoteDeadline >= todayYmd ? initial.quoteDeadline : ''} /></label>
      <label className="field wide"><span className="label">What pre-sales should prepare</span>
        <textarea name="scope" rows={3} maxLength={4000} placeholder="Systems and scope, brands the customer prefers, special requirements, who to contact…" /></label>
      {error && <p className="form-error wide" role="alert">{error}</p>}
      <div className="actions wide">
        <button type="button" className="btn pill-btn" onClick={onCancel}>Cancel</button>
        <button className="btn primary pill-btn" disabled={save.isPending}>{save.isPending ? 'Handing off…' : 'Hand off to pre-sales'}</button>
      </div>
    </form>
  );
}

/** A deal whose project folder was made before the CRM: pick it instead of creating a new numbered one. */
export function LinkFolder({ opportunityId, onLinked, onError, label = 'This deal already has a folder? Link it instead of creating a new one', replace = false }: {
  opportunityId: string; onLinked: () => void; onError: (e: unknown) => void; label?: ReactNode;
  /** The deal is linked to the wrong SQ folder: point it to another one (nothing in OneDrive changes). */
  replace?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [folderId, setFolderId] = useState('');
  const options = useQuery({
    queryKey: ['folder-options', opportunityId],
    enabled: open,
    queryFn: () => api<{ folders: { id: string; name: string; linkedTo?: string | null }[] }>(`/api/opportunities/${opportunityId}/folder-options`).then((r) => r.folders),
  });
  const link = useMutation({
    mutationFn: () => api(`/api/opportunities/${opportunityId}/folder`, { method: 'POST', body: replace ? { folderId, replace: true } : { folderId } }),
    onSuccess: onLinked,
    onError,
  });
  if (!open) return <p className="small pad-x"><button className="link-btn" onClick={() => setOpen(true)}>{label}</button></p>;
  return (
    <div className="docs-upload">
      <label className="field"><span className="label">Existing project folder</span>
        <select value={folderId} onChange={(e) => setFolderId(e.target.value)}>
          <option value="">{options.isPending ? 'Loading…' : 'Choose…'}</option>
          {/* Folders linked to another deal are shown greyed, with that deal's name. */}
          {(options.data ?? []).map((f) => <option key={f.id} value={f.id} disabled={!!f.linkedTo}>{f.linkedTo ? `${f.name}  (linked to ${f.linkedTo})` : f.name}</option>)}
        </select>
      </label>
      <button className="btn" disabled={!folderId || link.isPending} onClick={async () => { if (!replace || (await confirmDialog('Link the deal to this folder instead? Nothing in OneDrive is moved or deleted; new files and the costing file will come from this folder.'))) link.mutate(); }}>{replace ? 'Use this folder' : 'Link folder'}</button>
      <button className="btn ghost" onClick={() => setOpen(false)}>Cancel</button>
      {options.isError && <span className="form-error small">{errMsg(options.error)}</span>}
    </div>
  );
}
/** Deals from before the CRM: their documents are already in OneDrive, so they skip "Waiting for documents". */
export function ExistingDocsForm({ opportunityId, onDone, onCancel }: { opportunityId: string; onDone: () => void; onCancel: () => void }) {
  const engineers = useQuery({ queryKey: ['presales-engineers'], queryFn: () => api<{ users: { id: string; name: string }[] }>('/api/presales/engineers').then((r) => r.users) });
  const [status, setStatus] = useState<'ready' | 'in_progress' | 'quotation_ready'>('in_progress');
  const [userId, setUserId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    // Not started yet: it goes to the pre-sales team and the manager assigns the engineer.
    mutationFn: () => api(`/api/opportunities/${opportunityId}/presales/existing-documents`, { method: 'POST', body: { status, presalesUserId: status === 'ready' ? null : userId || null } }),
    onSuccess: onDone,
    onError: (e) => setError(errMsg(e)),
  });
  return (
    <form className="existing-docs" onSubmit={(e) => { e.preventDefault(); setError(null); save.mutate(); }}>
      <p className="small muted">The documents are already in OneDrive (deal from the old CRM). Where is pre-sales on it?</p>
      <label className="field"><span className="label">Pre-sales</span>
        <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
          <option value="ready">Not started yet</option>
          <option value="in_progress">Working on it</option>
          <option value="quotation_ready">Quotation already done</option>
        </select>
      </label>
      {status === 'ready' ? (
        <p className="small">Goes to the <b>pre-sales team</b>: the pre-sales manager assigns the engineer and the internal deadline, and you are told who is working on it.</p>
      ) : (
        <label className="field"><span className="label">Who worked on it in the old CRM *</span>
          <select value={userId} required onChange={(e) => setUserId(e.target.value)}>
            <option value="">Choose…</option>
            {(engineers.data ?? []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </label>
      )}
      {error && <p className="form-error" role="alert">{error}</p>}
      <div className="actions">
        <button type="button" className="btn small" onClick={onCancel}>Cancel</button>
        <button className="btn small primary" disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</button>
      </div>
    </form>
  );
}
